import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, FileText, Package, BadgeCheck, ChevronLeft,
  Calendar, Building2, FileJson, Plus, ShieldCheck, Zap, TrendingUp, CreditCard
} from 'lucide-react';
import { purchaseOrdersApi } from '../services/api';
import ExcelExportButton from '../components/ExcelExportButton';
import { useTheme } from '../context/ThemeContext';

function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const existingOrders = JSON.parse(localStorage.getItem('mock_purchase_orders') || '[]');
      const localOrder = existingOrders.find(o => o.id === parseInt(id));

      if (localOrder) {
        setOrder({
          id: localOrder.id,
          billNumber: localOrder.invoiceNumber,
          billDate: localOrder.dateTime.split(',')[0],
          companyName: localOrder.companyName,
          gstNumber: localOrder.gstin,
          status: localOrder.status || 'Delivered',
          purchaseItems: localOrder.purchaseItems || [
            { productName: 'Shilajit 30-Gold 40 Cap.', batchNumber: 'B-34567', mrp: 6200, quantity: 50, discount: 10, gst: 18 },
            { productName: 'Aloe Vera Nectar', batchNumber: 'B-34567', mrp: 450, quantity: 140, discount: 10, gst: 18 }
          ]
        });
        return;
      }

      const mockBills = [
        { id: 1, billNo: '#BILL-2023-001', company: 'Modern Traders', gstin: '27AAAAA0000A1Z5', date: 'Oct 24, 2023', amount: '45,250.00' },
        { id: 2, billNo: '#BILL-2023-002', company: 'Aarogya Pharma', gstin: '09BBBBB1111B2Z6', date: 'Oct 25, 2023', amount: '1,12,800.00' },
        { id: 3, billNo: '#BILL-2023-003', company: 'Global Logistics', gstin: '33CCCCC2222C3Z7', date: 'Oct 26, 2023', amount: '8,400.00' },
        { id: 10, billNo: '#BILL-2023-004', company: 'Tech Innovations', gstin: '18DDDDD3333D4Z8', date: 'Oct 27, 2023', amount: '62,500.00' }
      ];

      const staticBill = mockBills.find(b => b.id === parseInt(id));
      if (staticBill) {
        setOrder({
          id: staticBill.id,
          billNumber: staticBill.billNo,
          billDate: staticBill.date,
          companyName: staticBill.company,
          gstNumber: staticBill.gstin,
          status: 'Delivered',
          purchaseItems: [
            { productName: 'Generic Product A', batchNumber: 'B-999', mrp: parseFloat(staticBill.amount.replace(/,/g, '')) / 10, quantity: 10, discount: 0, gst: 18 },
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.mrp * item.quantity;
    const discountAmount = (subtotal * item.discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const gstAmount = (afterDiscount * item.gst) / 100;
    return afterDiscount + gstAmount;
  };

  const calculateSubtotal = () => order.purchaseItems.reduce((sum, item) => sum + (item.mrp * item.quantity), 0);
  const calculateTotalDiscount = () => order.purchaseItems.reduce((sum, item) => sum + ((item.mrp * item.quantity * item.discount) / 100), 0);
  const calculateTotalTax = () => order.purchaseItems.reduce((sum, item) => {
    const itemSubtotal = item.mrp * item.quantity;
    const discountAmount = (itemSubtotal * item.discount) / 100;
    return sum + ((itemSubtotal - discountAmount) * item.gst) / 100;
  }, 0);
  const calculateGrandTotal = () => calculateSubtotal() - calculateTotalDiscount() + calculateTotalTax();

  const handleDownloadPDF = () => navigate(`/purchase/invoice/${id}?download=true`);

  if (loading || !order) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0F172A]' : 'bg-[#F4F9F8]'}`}>
        <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: currentTheme.color }}></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-8 font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div className="flex items-center space-x-8">
          <button
            onClick={() => navigate('/purchase')}
            className={`w-14 h-14 rounded-2xl flex items-center justify-center border shadow-xl transition-all active:scale-90 ${isDark ? 'bg-[#111827] border-white/5 hover:bg-white/10' : 'bg-white border-gray-100 hover:bg-gray-50'}`}
          >
            <ChevronLeft size={28} style={{ color: currentTheme.color }} />
          </button>
          <div>
             <h1 className={`text-4xl font-black uppercase tracking-tighter ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>#{id === '1' ? '23456' : order.billNumber}</h1>
             <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 mt-1">Acquisition Manifest • Registry Entry</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
           <div className={`px-8 py-2 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-inner ${isDark ? 'bg-white/5 text-green-400' : 'bg-green-50 text-green-600'}`}>
             STATUS: {order.status}
           </div>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-10">
        {/* Left Column */}
        <div className="flex-1 space-y-10">
          
          {/* Bill Details Grid */}
          <div className={`p-10 rounded-[40px] shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center justify-between mb-10 pb-6 border-b border-white/5">
              <div className="flex items-center space-x-4">
                <FileText size={24} style={{ color: currentTheme.color }} />
                <h2 className={`text-xs font-black uppercase tracking-[0.3em] ${isDark ? 'text-gray-200' : 'text-[#1E4D43]'}`}>Procurement Identity</h2>
              </div>
              <button onClick={handleDownloadPDF} className="flex items-center gap-2 px-6 py-3 rounded-xl text-white text-[10px] font-black uppercase tracking-widest shadow-xl transition-all active:scale-95" style={{ backgroundColor: currentTheme.color }}>
                 <Download size={16} />
                 <span>Export Ledger</span>
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
              {[
                { label: 'Manifest ID', val: order.billNumber, icon: Zap },
                { label: 'Registry Date', val: order.billDate, icon: Calendar },
                { label: 'Provider', val: order.companyName, icon: Building2 },
                { label: 'Tax Context', val: order.gstNumber, icon: ShieldCheck },
              ].map((item, i) => (
                <div key={i} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <item.icon size={12} className="text-gray-500" />
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">{item.label}</p>
                  </div>
                  <p className={`text-base font-black truncate ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>{item.val}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Products Tactical Listing */}
          <div className={`p-10 rounded-[40px] shadow-2xl border overflow-hidden ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center space-x-4 mb-10">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-xl" style={{ backgroundColor: currentTheme.color }}>
                <Plus size={20} strokeWidth={3} />
              </div>
              <h2 className={`text-xs font-black uppercase tracking-[0.3em] ${isDark ? 'text-gray-200' : 'text-[#1E4D43]'}`}>Inventory Allocation</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`text-left border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <th className="pb-6 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Material Specs</th>
                    <th className="pb-6 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] text-center">Unit Val</th>
                    <th className="pb-6 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] text-center">Units</th>
                    <th className="pb-6 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] text-center">Rebate</th>
                    <th className="pb-6 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] text-center">Levy</th>
                    <th className="pb-6 text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] text-right">Aggregate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/2">
                  {order.purchaseItems.map((item, index) => (
                    <tr key={index} className="group transition-all hover:bg-white/2">
                      <td className="py-8">
                        <div className="flex items-center space-x-5">
                          <div className={`w-14 h-14 rounded-[1.25rem] flex items-center justify-center border shadow-xl ${isDark ? 'bg-white/5 border-white/5' : 'bg-[#F2FBF9] border-gray-100'}`}>
                             <Package size={28} style={{ color: currentTheme.color }} />
                          </div>
                          <div>
                            <p className={`text-base font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-[#1E4D43]'}`}>{item.productName}</p>
                            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1">BATCH: {item.batchNumber}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-8 text-center">
                        <span className="text-sm font-black tabular-nums" style={{ color: currentTheme.color }}>₹{item.mrp.toLocaleString()}</span>
                      </td>
                      <td className={`py-8 text-center font-black text-sm tabular-nums ${isDark ? 'text-gray-200' : 'text-[#1E4D43]'}`}>
                        {item.quantity}
                      </td>
                      <td className="py-8 text-center">
                        <span className="text-sm font-black text-green-500">{item.discount}%</span>
                      </td>
                      <td className={`py-8 text-center font-black text-sm ${isDark ? 'text-gray-400' : 'text-[#1E4D43]'}`}>
                        {item.gst}%
                      </td>
                      <td className="py-8 text-right font-black text-lg tabular-nums" style={{ color: currentTheme.color }}>
                        ₹{calculateItemTotal(item).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column - Summary Matrix */}
        <div className="w-full xl:w-[420px]">
          <div className={`p-10 rounded-[50px] shadow-2xl border sticky top-8 transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center space-x-4 mb-12">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl ${isDark ? 'bg-white/5' : 'bg-[#E6F4F1]'}`}>
                <BadgeCheck size={24} style={{ color: currentTheme.color }} />
              </div>
              <h2 className={`text-sm font-black uppercase tracking-[0.3em] ${isDark ? 'text-gray-200' : 'text-[#1E4D43]'}`}>Financial Matrix</h2>
            </div>

            <div className="space-y-8">
              {[
                { label: 'Sub-Total Ledger', val: calculateSubtotal(), icon: TrendingUp },
                { label: 'System Levy (GST 18%)', val: calculateTotalTax(), icon: ShieldCheck },
                { label: 'Rebate Allowance', val: calculateTotalDiscount(), icon: CreditCard, isNegative: true },
              ].map((s, i) => (
                <div key={i} className="flex justify-between items-center group">
                   <div className="flex items-center gap-3">
                      <s.icon size={14} className="text-gray-500 opacity-50 group-hover:opacity-100 transition-opacity" />
                      <span className="text-xs font-black text-gray-500 uppercase tracking-widest">{s.label}</span>
                   </div>
                   <span className={`text-base font-black tabular-nums ${s.isNegative ? 'text-red-500' : (isDark ? 'text-white' : 'text-[#1E4D43]')}`}>
                      {s.isNegative ? '-' : ''}₹{Math.round(s.val).toLocaleString('en-IN')}
                   </span>
                </div>
              ))}

              <div className={`pt-10 mt-10 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.4em] mb-4 text-center">Total Net Value</p>
                <div className="relative text-center">
                   <p className="text-5xl font-black tabular-nums text-green-500 tracking-tighter drop-shadow-2xl">
                     ₹{calculateGrandTotal().toLocaleString('en-IN')}
                   </p>
                   <div className="absolute inset-0 bg-green-500/10 blur-[60px] rounded-full -z-10"></div>
                </div>
              </div>

              <div className="pt-10 flex flex-col gap-4">
                 <button onClick={handleDownloadPDF} className="w-full py-5 rounded-[28px] text-white font-black uppercase tracking-[0.3em] text-[11px] shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-3" style={{ backgroundColor: currentTheme.color }}>
                    <Download size={18} />
                    <span>Download Manifest</span>
                 </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseOrderDetail;
